import Phaser from 'phaser';
import { GEODE_FRAME, VIEWPORT_W } from '../../../constants';
import { onGame, offGame, GameEvents } from '../../../events';
import { playerState } from '../../../player-state';
import type { PlayerBoogaModel } from '../../types';
import { buildPlayerCompositeTexture, removePlayerCompositeTexture } from './player-load';
import { PLAYER_COMPOSITE_ANIM, PLAYER_COMPOSITE_TEXTURE, hasPlayerCompositeTexture } from './player-mobile-bake';

const PLAYER_BOOGA_VISIBLE = true;

function equipmentSignature() {
  const { head, body, weapon } = playerState.equipped;
  return `${head ?? ''}|${body ?? ''}|${weapon ?? ''}`;
}

function playerPanelVisible(scrollX: number) {
  return scrollX < VIEWPORT_W * 0.5;
}

export function createPlayerView(scene: Phaser.Scene, getModel: () => PlayerBoogaModel) {
  const container = scene.add.container(0, 0).setDepth(10).setVisible(PLAYER_BOOGA_VISIBLE);

  let equipSig = equipmentSignature();
  let sprite: Phaser.GameObjects.Sprite | null = null;
  let building = false;
  /** Deferred until explore panel — full-res bake at boot jetsams iOS Safari. */
  let builtOnce = false;
  let pendingRebuild = false;

  function applyLayout() {
    const { x, y, scale } = getModel();
    container.setPosition(x, y);
    container.setScale(scale);
  }

  function layoutSprite() {
    if (!sprite?.active) return;
    const fw = sprite.frame?.width || GEODE_FRAME;
    const scale = GEODE_FRAME / fw;
    sprite.setDisplaySize(Math.round(fw * scale), Math.round((sprite.frame?.height || fw) * scale));
    sprite.setOrigin(0, 0);
    sprite.setPosition(0, 0);
  }

  function destroySprite() {
    sprite?.destroy();
    sprite = null;
  }

  function syncVisibility(scrollX: number) {
    if (!PLAYER_BOOGA_VISIBLE) {
      container.setVisible(false);
      return;
    }
    const visible = playerPanelVisible(scrollX);
    container.setVisible(visible);
    if (visible && (!builtOnce || pendingRebuild)) {
      void rebuild();
    }
  }

  async function rebuild() {
    if (!PLAYER_BOOGA_VISIBLE) return;
    if (building) return;
    if (!playerPanelVisible(scene.cameras.main.scrollX)) {
      pendingRebuild = true;
      return;
    }

    building = true;
    pendingRebuild = false;
    try {
      destroySprite();
      removePlayerCompositeTexture(scene);

      const ok = await buildPlayerCompositeTexture(scene, 'Standing');
      builtOnce = true;
      if (!ok || !hasPlayerCompositeTexture(scene)) return;

      sprite = scene.add.sprite(0, 0, PLAYER_COMPOSITE_TEXTURE);
      layoutSprite();
      if (scene.anims.exists(PLAYER_COMPOSITE_ANIM)) {
        sprite.play(PLAYER_COMPOSITE_ANIM, true);
      }
      container.add(sprite);
    } finally {
      building = false;
    }
  }

  async function refreshEquipment() {
    if (!PLAYER_BOOGA_VISIBLE) return;
    const nextSig = equipmentSignature();
    if (nextSig === equipSig && sprite?.active) return;
    equipSig = nextSig;
    await rebuild();
  }

  async function init() {
    applyLayout();
    if (!PLAYER_BOOGA_VISIBLE) {
      container.setVisible(false);
      return;
    }
    // Do not bake at boot — default panel is smash; bake on first swipe to explore.
    syncVisibility(scene.cameras.main.scrollX);
  }

  const onHudRefresh = () => {
    void refreshEquipment();
  };

  onGame(GameEvents.HUD_REFRESH, onHudRefresh);

  applyLayout();

  return {
    container,
    init,
    setModel() {
      applyLayout();
    },
    syncFromScroll(scrollX: number) {
      syncVisibility(scrollX);
    },
    refreshEquipment,
    destroy() {
      offGame(GameEvents.HUD_REFRESH, onHudRefresh);
      destroySprite();
      removePlayerCompositeTexture(scene);
      container.destroy();
    },
  };
}
